import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Skeleton, Tooltip } from '@blade/ui-kit'
import { authStateAtom } from '@root/entities/auth'
import { CurrencyModel } from '@root/entities/currency'
import {
  jitoAverageTipStateAtom,
  Network,
  priorityFeesAtom,
  selectedNetworkAtom,
} from '@root/entities/network'
import { NetworkIcon } from '@root/entities/network/ui'
import { walletsModelAtom } from '@root/entities/wallet'
import { AmountFormatted, NumericFormat } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { PetrolIcon, SharingHandIcon } from '@root/shared/ui-kit'

import type { ClassNameProps } from '@root/shared/react'
import type BigNumber from 'bignumber.js'

type Props = {} & ClassNameProps

export const TopbarBalanceFees: React.FC<Props> = () => {
  const { t } = useTranslation()
  const [walletsModel] = useAtom(walletsModelAtom)
  const [isLoggedIn] = useAtom(authStateAtom.isLoggedIn)

  const [network] = useAtom(selectedNetworkAtom)
  const [priorityFees] = useAtom(priorityFeesAtom)
  const [jitoAverageTip] = useAtom(jitoAverageTipStateAtom)
  const [balance] = useAtom(walletsModel.nativeAssetBalance)

  const priorityFeeFormatted = useMemo(
    () =>
      priorityFees?.recommended
        ? formatFee(priorityFees.recommended)
        : undefined,
    [priorityFees],
  )

  const renderJitoAverageTip = () => {
    if (network !== Network.Solana) {
      return undefined
    }

    const tip = jitoAverageTip.value
    return (
      <Item>
        <SharingHandIcon size={16} />
        {tip ? (
          formatFee(tip.recommended)
        ) : (
          <Skeleton height={16} width={34} paddingBlock={2} />
        )}
      </Item>
    )
  }

  const priorityFeeTooltip = useMemo(() => {
    const amount = priorityFees?.recommended
    if (!amount) {
      return undefined
    }

    const symbol = match(network)
      .with(Network.Solana, () => CurrencyModel.Sol.ticker)
      .with(Network.Ton, () => CurrencyModel.Ton.ticker)
      .with(Network.Ethereum, () => 'GWEI')
      .with(Network.Base, () => 'GWEI')
      .with(Network.Bsc, () => 'GWEI')
      .exhaustive()

    return t('network-data.priority-fee-tooltip', {
      amount: formatFee(amount),
      ticker: symbol,
    })
  }, [network, priorityFees?.recommended, t])

  return (
    <Root>
      {isLoggedIn ? (
        <Item>
          <AmountFormatted amount={balance} roundingMode="trunc" />
          <NetworkIcon network={network} size={14} />
        </Item>
      ) : undefined}
      <Tooltip content={priorityFeeTooltip}>
        <Item>
          <PetrolIcon size={16} />
          {priorityFeeFormatted ? (
            priorityFeeFormatted
          ) : (
            <Skeleton height={16} width={32} paddingBlock={2} />
          )}
        </Item>
      </Tooltip>
      {renderJitoAverageTip()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  align-items: center;
  gap: 6px;

  justify-self: start;
`

const Item = styled.div`
  color: var(--text-11);

  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;

  gap: 2px;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
`

// Requested by Ivan

const formatFee = (fee: BigNumber) => {
  return NumericFormat.format(fee, {
    mantissa: 4,
    trimMantissa: true,
  })
}
